import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'

const MAX_CONTINUOUS = 2

type ReleaseFn = () => void

type ContinuousMotionContextValue = {
  /** Returns a release function, or null if the global budget is exhausted. */
  acquire: () => ReleaseFn | null
}

const ContinuousMotionContext = createContext<ContinuousMotionContextValue | null>(null)

/**
 * Limits concurrent infinite CSS animations (marquee / float / orbit) under `.ref-storefront`
 * so long pages stay performant. Consumers outside this provider behave as before (no cap).
 */
export function ContinuousMotionProvider({ children }: { children: ReactNode }) {
  const countRef = useRef(0)

  const acquire = useCallback((): ReleaseFn | null => {
    if (countRef.current >= MAX_CONTINUOUS) return null
    countRef.current += 1
    let done = false
    return () => {
      if (done) return
      done = true
      countRef.current = Math.max(0, countRef.current - 1)
    }
  }, [])

  const value = useMemo(() => ({ acquire }), [acquire])

  return <ContinuousMotionContext.Provider value={value}>{children}</ContinuousMotionContext.Provider>
}

/**
 * Try to take one “continuous motion” slot for the lifetime of the component.
 * When `false`, render the same UI without infinite keyframe classes.
 */
export function useContinuousMotionSlot(enabled: boolean): boolean {
  const ctx = useContext(ContinuousMotionContext)
  const [allowed, setAllowed] = useState(() => !ctx || !enabled)

  useLayoutEffect(() => {
    if (!enabled) {
      setAllowed(true)
      return
    }
    if (!ctx) {
      setAllowed(true)
      return
    }
    const release = ctx.acquire()
    if (!release) {
      setAllowed(false)
      return () => {}
    }
    setAllowed(true)
    return () => release()
  }, [ctx, enabled])

  return allowed
}
